/**
 * WebAuthn Utility Functions
 *
 * Helper functions for passkey authentication (Touch ID, Face ID, Windows Hello).
 * Provides browser detection, capability checking, and error handling.
 */

import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

// Platform detection constants
const PLATFORM_NAMES = {
  MAC: 'Safari on macOS',
  WINDOWS: 'Windows Hello',
  IOS: 'Face ID / Touch ID',
  ANDROID: 'Android Passkey',
  DEFAULT: 'Passkey Authentication'
};

const PLATFORM_IDENTIFIERS = {
  MAC: ['Mac'],
  WINDOWS: ['Win'],
  IOS: ['iPhone', 'iPad'],
  ANDROID: ['Android']
};

// Biometric icon identifiers for UI display
const BIOMETRIC_ICONS = {
  FINGERPRINT: 'fingerprint',
  SHIELD: 'shield',
  FACE: 'face'
};

// WebAuthn error names
const WEBAUTHN_ERROR_NAMES = {
  NOT_ALLOWED: 'NotAllowedError',
  ABORT: 'AbortError',
  SECURITY: 'SecurityError',
  NOT_SUPPORTED: 'NotSupportedError'
};

// Error message keywords for detection
const ERROR_KEYWORDS = {
  CANCELLED: 'cancelled',
  TIMEOUT: 'timeout',
  NO_CREDENTIALS: ['No credentials', 'not found'],
  SENSOR: ['sensor', 'hardware']
};

// User-friendly error messages
const ERROR_MESSAGES = {
  CANCELLED: 'Passkey authentication was cancelled. Please try again.',
  TIMEOUT: 'Authentication timed out. Please try again.',
  NO_CREDENTIALS: 'No passkeys found. Please register first.',
  SENSOR: 'Could not access passkey authentication (Touch ID, Face ID, or Windows Hello). Please try again or use password.',
  SECURITY: 'Passkey authentication requires HTTPS in production or HTTP on localhost/127.0.0.1 in development.',
  UNKNOWN: 'An unknown error occurred'
};

/**
 * Check if WebAuthn is supported in the current browser.
 * 
 * @returns {boolean} True if WebAuthn is supported
 */
export const isWebAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined;
};

/**
 * Check if we're on a mobile device.
 *
 * @returns {boolean} True if on mobile
 */
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  // Check for mobile user agents
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
};

/**
 * Check if platform authenticator (Touch ID, Face ID, Windows Hello) is available.
 *
 * On mobile devices, isUserVerifyingPlatformAuthenticatorAvailable() can return false
 * even when passkeys work perfectly via synced credentials (iCloud Keychain, Google Password Manager).
 * So on mobile, we're more permissive and just check for basic WebAuthn support.
 *
 * @returns {Promise<boolean>} True if platform authenticator is available
 */
export const isPlatformAuthenticatorAvailable = async () => {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    // On mobile devices, be more permissive - synced passkeys work even when
    // isUserVerifyingPlatformAuthenticatorAvailable returns false
    if (isMobileDevice()) {
      // Check if conditional mediation is available (better indicator on mobile)
      if (window.PublicKeyCredential.isConditionalMediationAvailable) {
        const conditionalAvailable = await window.PublicKeyCredential.isConditionalMediationAvailable();
        if (conditionalAvailable) {
          return true;
        }
      }
      // Even if conditional mediation isn't available, passkeys may still work
      // on mobile via iCloud Keychain or Google Password Manager
      // Return true to show the option and let the browser handle it
      return true;
    }

    // On desktop, use the standard check
    const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (error) {
    console.error('Error checking platform authenticator availability:', error);
    // On error, still allow on mobile since passkeys often work
    return isMobileDevice();
  }
};

/**
 * Get user-friendly browser/platform name for display.
 * Uses userAgent for better mobile detection since navigator.platform is deprecated.
 *
 * @returns {string} Browser/platform name (e.g., "Safari on macOS")
 */
export const getPlatformName = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check mobile first (userAgent is more reliable than platform on mobile)
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return PLATFORM_NAMES.IOS;
  } else if (/android/.test(userAgent)) {
    return PLATFORM_NAMES.ANDROID;
  }

  // For desktop, check platform and userAgent
  const platform = navigator.platform || '';

  if (PLATFORM_IDENTIFIERS.MAC.some(id => platform.includes(id)) || /macintosh|mac os x/.test(userAgent)) {
    return PLATFORM_NAMES.MAC;
  } else if (PLATFORM_IDENTIFIERS.WINDOWS.some(id => platform.includes(id)) || /windows/.test(userAgent)) {
    return PLATFORM_NAMES.WINDOWS;
  }

  return PLATFORM_NAMES.DEFAULT;
};

/**
 * Get user-friendly error message for WebAuthn errors.
 *
 * @param {Error} error - Error from WebAuthn operation
 * @returns {string} User-friendly error message
 */
export const getWebAuthnErrorMessage = (error) => {
  if (!error) return ERROR_MESSAGES.UNKNOWN;

  const errorName = error.name || '';
  const errorMessage = error.message || '';

  // User cancelled
  if (errorName === WEBAUTHN_ERROR_NAMES.NOT_ALLOWED || errorMessage.includes(ERROR_KEYWORDS.CANCELLED)) {
    return ERROR_MESSAGES.CANCELLED;
  }

  // Timeout
  if (errorName === WEBAUTHN_ERROR_NAMES.ABORT || errorMessage.includes(ERROR_KEYWORDS.TIMEOUT)) {
    return ERROR_MESSAGES.TIMEOUT;
  }

  // No credentials
  if (ERROR_KEYWORDS.NO_CREDENTIALS.some(keyword => errorMessage.includes(keyword))) {
    return ERROR_MESSAGES.NO_CREDENTIALS;
  }

  // Sensor issue
  if (ERROR_KEYWORDS.SENSOR.some(keyword => errorMessage.includes(keyword))) {
    return ERROR_MESSAGES.SENSOR;
  }

  // Security error (often related to HTTPS or domain issues)
  if (errorName === WEBAUTHN_ERROR_NAMES.SECURITY || errorName === WEBAUTHN_ERROR_NAMES.NOT_SUPPORTED) {
    return ERROR_MESSAGES.SECURITY;
  }

  // Generic error
  return `Authentication error: ${errorMessage}`;
};

/**
 * Start biometric registration flow.
 *
 * @param {Object} options - Registration options from backend
 * @returns {Promise<Object>} Registration response to send to backend
 * @throws {Error} If registration fails
 */
export const registerBiometric = async (options) => {
  try {
    const response = await startRegistration(options);
    return response;
  } catch (error) {
    console.error('Registration error:', error);
    throw new Error(getWebAuthnErrorMessage(error));
  }
};

/**
 * Start biometric authentication flow.
 * 
 * @param {Object} options - Authentication options from backend
 * @returns {Promise<Object>} Authentication response to send to backend
 * @throws {Error} If authentication fails
 */
export const authenticateBiometric = async (options) => {
  try {
    const response = await startAuthentication(options);
    return response;
  } catch (error) {
    console.error('Authentication error:', error);
    throw new Error(getWebAuthnErrorMessage(error));
  }
};

/**
 * Get icon name based on platform for UI display.
 * Uses userAgent for better mobile detection since navigator.platform is deprecated.
 *
 * @returns {string} Icon identifier
 */
export const getBiometricIcon = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check mobile first (userAgent is more reliable)
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return BIOMETRIC_ICONS.FACE; // Face ID icon
  } else if (/android/.test(userAgent)) {
    return BIOMETRIC_ICONS.FINGERPRINT; // Android fingerprint
  }

  // Desktop checks
  const platform = navigator.platform || '';

  if (PLATFORM_IDENTIFIERS.MAC.some(id => platform.includes(id)) || /macintosh|mac os x/.test(userAgent)) {
    return BIOMETRIC_ICONS.FINGERPRINT; // Touch ID icon
  } else if (PLATFORM_IDENTIFIERS.WINDOWS.some(id => platform.includes(id)) || /windows/.test(userAgent)) {
    return BIOMETRIC_ICONS.SHIELD; // Windows Hello icon
  }

  return BIOMETRIC_ICONS.FINGERPRINT; // Default biometric icon
};
